import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle
} from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import {
  MessageSquare,
  QrCode,
  RefreshCw,
  CheckCircle,
  AlertCircle,
  Link as LinkIcon,
  Copy,
  Send,
  Power,
  Smartphone
} from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SynklyWhatsAppPage = () => {
  const [status, setStatus] = useState(null);
  const [webhook, setWebhook] = useState(null);
  const [loading, setLoading] = useState(true);
  const [connecting, setConnecting] = useState(false);
  const [testPhone, setTestPhone] = useState('');
  const [testLoading, setTestLoading] = useState(false);
  const pollRef = useRef(null);

  const fetchStatus = async () => {
    try {
      const response = await axios.get(`${API}/synkly/whatsapp/status`);
      setStatus(response.data);
      return response.data;
    } catch (error) {
      console.error('Erro ao verificar status do WhatsApp:', error);
      setStatus({ configured: false, status: 'error' });
      return null;
    }
  };

  const fetchWebhook = async () => {
    try {
      const response = await axios.get(`${API}/synkly/whatsapp/webhook-url`);
      setWebhook(response.data);
    } catch (error) {
      console.error('Erro ao obter URL do webhook:', error);
    }
  };

  useEffect(() => {
    (async () => {
      setLoading(true);
      await Promise.all([fetchStatus(), fetchWebhook()]);
      setLoading(false);
    })();
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, []);

  const startPolling = () => {
    if (pollRef.current) clearInterval(pollRef.current);
    pollRef.current = setInterval(async () => {
      const data = await fetchStatus();
      if (data && data.status === 'connected') {
        clearInterval(pollRef.current);
        pollRef.current = null;
        setConnecting(false);
        toast.success('WhatsApp conectado com sucesso! 🎉');
      }
    }, 3000);
  };

  const handleConnect = async () => {
    setConnecting(true);
    try {
      await axios.post(`${API}/synkly/whatsapp/connect`);
      toast.info('Escaneie o QR Code com o WhatsApp do seu celular.');
      await fetchStatus();
      startPolling();
    } catch (error) {
      const detail = error.response?.data?.detail || 'Erro ao iniciar conexão.';
      toast.error(detail);
      setConnecting(false);
    }
  };

  const handleDisconnect = async () => {
    try {
      await axios.delete(`${API}/synkly/whatsapp/disconnect`);
      if (pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
      setConnecting(false);
      toast.success('WhatsApp desconectado.');
      await fetchStatus();
    } catch (error) {
      toast.error('Erro ao desconectar.');
    }
  };

  const handleTest = async () => {
    if (!testPhone.trim()) {
      toast.error('Informe um número de telefone.');
      return;
    }
    setTestLoading(true);
    try {
      await axios.post(`${API}/synkly/whatsapp/test`, { phone: testPhone.trim() });
      toast.success('Mensagem de teste enviada!');
    } catch (error) {
      const detail = error.response?.data?.detail || 'Erro ao enviar mensagem de teste.';
      toast.error(detail);
    } finally {
      setTestLoading(false);
    }
  };

  const copyWebhook = () => {
    if (webhook?.webhook_url) {
      navigator.clipboard.writeText(webhook.webhook_url);
      toast.success('URL copiada!');
    }
  };

  const copySecret = () => {
    if (webhook?.secret) {
      navigator.clipboard.writeText(webhook.secret);
      toast.success('Segredo copiado!');
    }
  };

  const renderQr = (qr) => {
    if (!qr) return null;
    const src = qr.startsWith('data:') ? qr : `data:image/png;base64,${qr}`;
    return (
      <div className="flex flex-col items-center gap-3 py-4">
        <img src={src} alt="QR Code WhatsApp" className="w-64 h-64 border rounded-lg bg-white p-2" />
        <p className="text-sm text-gray-500 text-center">
          Abra o WhatsApp no celular &gt; Aparelhos conectados &gt; Conectar um aparelho
        </p>
      </div>
    );
  };

  const statusBadge = () => {
    const s = status?.status;
    if (s === 'connected') {
      return <Badge className="bg-green-100 text-green-800"><CheckCircle className="w-3 h-3 mr-1" /> Conectado</Badge>;
    }
    if (s === 'qr' || s === 'connecting') {
      return <Badge className="bg-yellow-100 text-yellow-800"><QrCode className="w-3 h-3 mr-1" /> Aguardando conexão</Badge>;
    }
    if (s === 'error') {
      return <Badge className="bg-red-100 text-red-800"><AlertCircle className="w-3 h-3 mr-1" /> Erro</Badge>;
    }
    return <Badge className="bg-gray-100 text-gray-700">Desconectado</Badge>;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-6 h-6 animate-spin text-gray-400" />
      </div>
    );
  }

  const isConnected = status?.status === 'connected';
  const showQr = status?.qr && !isConnected;

  return (
    <div className="max-w-3xl mx-auto p-4 space-y-6">
      <div className="flex items-center gap-3">
        <div className="p-2 bg-green-100 rounded-lg">
          <MessageSquare className="w-6 h-6 text-green-600" />
        </div>
        <div>
          <h1 className="text-2xl font-bold">Conexão WhatsApp</h1>
          <p className="text-gray-500">Conecte o WhatsApp da empresa para a Maya conversar com seus colaboradores.</p>
        </div>
      </div>

      {!status?.configured && (
        <Card className="border-yellow-300 bg-yellow-50">
          <CardContent className="pt-6 flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-yellow-600 mt-0.5" />
            <div className="text-sm text-yellow-800">
              A integração ainda não está configurada. É necessário cadastrar a chave da API e o endereço da Synkly
              (variáveis <strong>SYNKLY_API_KEY</strong> e <strong>SYNKLY_BASE_URL</strong>) para habilitar a conexão.
            </div>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Smartphone className="w-5 h-5" /> Status da conexão
              </CardTitle>
              <CardDescription>
                {isConnected && status?.external_id
                  ? `Número conectado: ${status.external_id}`
                  : 'Conecte um número de WhatsApp para começar.'}
              </CardDescription>
            </div>
            {statusBadge()}
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {status?.last_error && (
            <div className="text-sm text-red-600">{status.last_error}</div>
          )}

          {showQr && renderQr(status.qr)}

          {connecting && !status?.qr && !isConnected && (
            <div className="flex items-center gap-2 text-gray-500 text-sm">
              <RefreshCw className="w-4 h-4 animate-spin" /> Gerando QR Code...
            </div>
          )}

          <div className="flex gap-3">
            {!isConnected ? (
              <Button onClick={handleConnect} disabled={!status?.configured || connecting}>
                {connecting ? (
                  <><RefreshCw className="w-4 h-4 mr-2 animate-spin" /> Conectando...</>
                ) : (
                  <><QrCode className="w-4 h-4 mr-2" /> {status?.qr ? 'Gerar novo QR' : 'Conectar WhatsApp'}</>
                )}
              </Button>
            ) : (
              <Button variant="destructive" onClick={handleDisconnect}>
                <Power className="w-4 h-4 mr-2" /> Desconectar
              </Button>
            )}
            <Button variant="outline" onClick={fetchStatus}>
              <RefreshCw className="w-4 h-4 mr-2" /> Atualizar
            </Button>
          </div>
        </CardContent>
      </Card>

      {isConnected && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Send className="w-5 h-5" /> Enviar mensagem de teste
            </CardTitle>
            <CardDescription>Envie uma mensagem da Maya para validar a conexão.</CardDescription>
          </CardHeader>
          <CardContent className="flex gap-3">
            <Input
              placeholder="Ex: 11999998888"
              value={testPhone}
              onChange={(e) => setTestPhone(e.target.value)}
            />
            <Button onClick={handleTest} disabled={testLoading}>
              {testLoading ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            </Button>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <LinkIcon className="w-5 h-5" /> Webhook (recebimento de mensagens)
          </CardTitle>
          <CardDescription>
            Cadastre esta URL no painel da Synkly para que a Maya responda automaticamente às mensagens recebidas.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <div>
            <p className="text-sm font-medium mb-1">URL do Webhook</p>
            <div className="flex gap-2">
              <Input readOnly value={webhook?.webhook_url || ''} className="font-mono text-sm" />
              <Button variant="outline" onClick={copyWebhook}>
                <Copy className="w-4 h-4" />
              </Button>
            </div>
          </div>
          <div>
            <p className="text-sm font-medium mb-1">Segredo (campo "secret" no painel da Synkly)</p>
            <div className="flex gap-2">
              <Input readOnly value={webhook?.secret || ''} className="font-mono text-sm" />
              <Button variant="outline" onClick={copySecret}>
                <Copy className="w-4 h-4" />
              </Button>
            </div>
          </div>
          <p className="text-sm text-gray-500">
            No painel da Synkly, cadastre a <strong>URL</strong> acima e cole este{' '}
            <strong>segredo</strong> no campo "secret". Isso ativa a validação de
            assinatura (HMAC): mensagens sem assinatura válida são recusadas.
            Eventos: <strong>message.received</strong>.
          </p>
        </CardContent>
      </Card>
    </div>
  );
};

export default SynklyWhatsAppPage;
